// src/config/pageAccess.js — GES-252 P4b (enforcement)
//
// THE ONE DECISION FUNCTION for "may this account see this surface?". Pure — no React,
// no fetch — so AuthContext can own the state and every screen asks the same question.
// P4a built the registry (src/config/pageRegistry.js); this is the half that acts on it.
//
// ── THE RULE (fail-closed) ───────────────────────────────────────────────────────────
//   is_system Admin          -> TRUE for everything (bypass; roles.is_system ignores the map)
//   visible_pages {"*":true} -> TRUE for everything (the wildcard 60/64 accounts carry)
//   key ticked true          -> TRUE
//   anything else            -> FALSE
// "Anything else" deliberately includes {} , a key that is not in the map, a key that is
// not in the registry at all, a role-less account (server sends {}), and the window before
// /api/me answers. There is NO fallback default: an account we cannot vouch for sees
// nothing and is told so, rather than being quietly granted the app.
//
// ── TABS ─────────────────────────────────────────────────────────────────────────────
// A tab key is `<page>:<tab id>` (P4a's shape). A tab is visible iff its OWN composite key
// passes AND its parent page passes — hiding a page implicitly hides its tabs, so a role
// granted `procurement:git` but not `procurement` does not get a tab it can never reach.
//
// ── THE THREE DRILL-DOWN DETAIL VIEWS ────────────────────────────────────────────────
// `site-detail` / `farm-detail` / `band-detail` are render keys in App.jsx's `pages` map
// with NO menu entry and NO route. The ONLY code that can put them into `currentPage` is
// App.jsx's openSite / openFarm / openBand (App.jsx:103-105), which exist solely as props
// on the six dashboard list/detail components. They also need `dashSel` (the row you
// clicked) to render anything at all. They are therefore NOT independently navigable, and
// per the P4b rule they INHERIT: a detail view is visible iff at least one page that can
// open it is visible. DETAIL_OPENERS below is the wiring, read off App.jsx:185-189, and
// the closure is computed rather than hand-flattened so it cannot drift from the table.
//
// Their TABS keep their own matrix rows and are gated by their own keys, like every other
// tab. That is deliberate: "may drill into a band" and "may see that band's Financial tab"
// are two different grants, and collapsing them would take a control away from the owner.

import { getRegistryEntry, hasWildcard, isAdminOnly, REGISTRY_PAGES, PAGE_REGISTRY } from './pageRegistry';

// Direct wiring, verbatim from App.jsx's `pages` map: which page's body carries the
// onOpenSite / onOpenFarm / onOpenBand prop that sets this detail view.
const DETAIL_OPENERS = {
  'site-detail': ['sites-list'],                                                      // SitesViewA onOpenSite
  'farm-detail': ['sites-list', 'site-detail', 'farms-list', 'band-detail'],           // …onOpenFarm
  'band-detail': ['sites-list', 'site-detail', 'farms-list', 'farm-detail', 'bands-list', 'band-detail'], // …onOpenBand
};

// Flatten each detail view's openers down to pages that are themselves menu-reachable,
// so the check never has to recurse at call time (band-detail opens farm-detail opens …).
const DETAIL_ROOTS = (() => {
  const out = {};
  for (const key of Object.keys(DETAIL_OPENERS)) {
    const seen = new Set();
    const roots = new Set();
    const stack = [...DETAIL_OPENERS[key]];
    while (stack.length) {
      const p = stack.pop();
      if (seen.has(p)) continue;
      seen.add(p);
      if (DETAIL_OPENERS[p]) stack.push(...DETAIL_OPENERS[p]);  // another detail view — keep walking
      else roots.add(p);                                        // a real menu page — that's a root
    }
    out[key] = [...roots];
  }
  return out;
})();

export const isDetailView = (key) => Object.prototype.hasOwnProperty.call(DETAIL_OPENERS, key);
export const detailRootsOf = (key) => DETAIL_ROOTS[key] || [];

// The menu pages, in NAV order, excluding the three drill-down views (they cannot be a
// landing page: without a clicked row there is nothing for them to show).
export const LANDABLE_PAGES = REGISTRY_PAGES.filter(p => !p.detail).map(p => p.page_key);

// A page's own tab keys, precomputed. Used by the "at least one tab" rule below.
const TAB_KEYS_OF = PAGE_REGISTRY.reduce((acc, r) => {
  if (r.parent) (acc[r.parent] = acc[r.parent] || []).push(r.page_key);
  return acc;
}, {});


// 41080a — A PARENT GRANTS ITS CHILDREN UNLESS ONE IS SINGLED OUT.
//
// The rule used to be "a page with tabs needs at least one tab ticked", which made ticking a page
// alone do NOTHING — the owner ticked Data Entry, saw no change, and reasonably read that as broken.
// It was also 20+ clicks to grant a page that has many children.
//
// So the reading is inverted: ticking a parent grants everything under it, and ticking any child
// NARROWS to the ticked ones. "Data Entry" means the whole of Data Entry; "Data Entry + Production"
// means only Production. The empty-tab-strip leak the old rule guarded against cannot happen either
// way — that came from granting a page while granting none of its tabs, which is now impossible.
//
// Still a RAW MAP READ over the parent's own children, never recursive, so a child asking whether a
// sibling was ticked cannot loop back through the parent.
// Expense Groups (db/892) is a sibling of Expense Categories: a role that ticked finance-subtypes outright keeps seeing both (no stored role is edited).
const TAB_ALIAS = { 'settings:expense-groups': 'settings:finance-subtypes' };
function anyChildTicked(parentKey, vp) {
  const keys = TAB_KEYS_OF[parentKey];
  return !!keys && keys.some(k => vp[k] === true);
}

/**
 * ctx = { isAdmin: boolean, visiblePages: object|null }
 * `visiblePages: null` means "not loaded / could not be loaded" -> nothing is visible.
 */
export function pageVisible(key, ctx) {
  if (!key) return false;
  if (ctx && ctx.isAdmin === true) return true;                       // is_system Admin bypass
  const vp = ctx && ctx.visiblePages;
  if (!vp || typeof vp !== 'object') return false;                    // not loaded / role-less / failed
  if (hasWildcard(vp)) return true;                                   // {"*": true} — "Full access"
  // The three drill-downs: INHERITED page access (an opener must be visible), own-key tabs.
  // The drill-downs inherit from whichever page can open them; their own tabs follow the rule below.
  if (isDetailView(key)) return detailRootsOf(key).some(r => vp[r] === true);
  const entry = getRegistryEntry(key);
  if (!entry) {
    // 41080a — AN UNREGISTERED CHILD INHERITS ITS PARENT, it does not vanish.
    //
    // The registry RESTATES the tab and report lists rather than importing them (they are inline
    // consts inside their pages), so someone adding a report and forgetting the registry is a matter
    // of time. Failing closed would make that new report invisible to everyone at once, and the blame
    // would land on roles rather than on the missing line. Inheriting means the worst case is
    // "not yet grantable on its own" — visible to whoever can see its parent, which is the decision
    // an admin actually made.
    //
    // Bounded deliberately: it inherits ONLY from a parent that IS registered, so an entirely unknown
    // key is still refused. A top-level key with no parent segment stays false.
    const cut = String(key).lastIndexOf(':');
    if (cut > 0) {
      const parentKey = key.slice(0, cut);
      if (getRegistryEntry(parentKey)) return pageVisible(parentKey, ctx);
    }
    return false;
  }
  if (entry.parent) {
    if (!pageVisible(entry.parent, ctx)) return false;                // the parent gates the child
    if (vp[key] === true) return true;                                // ticked outright
    if (TAB_ALIAS[key] && vp[TAB_ALIAS[key]] === true) return true;  // a role that sees the base tab sees its sibling
    return !anyChildTicked(entry.parent, vp);                         // else inherit, if none singled out
  }
  return vp[key] === true;
}

/** A tab, addressed as (page, tabId) rather than by its composite key. */
export function tabVisible(pageKey, tabId, ctx) {
  return pageVisible(`${pageKey}:${tabId}`, ctx);
}

/** The page an account should land on when its target is hidden. null = it has none. */
export function firstVisiblePage(ctx) {
  // GES-257 (41142c) — an adminOnly page is never a landing page for a non-admin. pageVisible
  // answers `true` for a {"*": true} "Full access" account on EVERY key, so without this clause a
  // wildcard non-admin whose earlier pages were all hidden could be landed on a page whose
  // endpoints will only ever answer 403. Today `dashboard` sorts first so this never fires — which
  // is exactly why it has to be written down rather than relied on.
  for (const key of LANDABLE_PAGES) {
    if (isAdminOnly(key) && !(ctx && ctx.isAdmin === true)) continue;
    if (pageVisible(key, ctx)) return key;
  }
  return null;
}

/** Does this account have anywhere at all to go? */
export function hasAnyVisiblePage(ctx) {
  return firstVisiblePage(ctx) !== null;
}
